import React, { CSSProperties, Dispatch, SetStateAction, useMemo, useRef, useState } from "react";
import { _, t, useClickOutside } from "../../lib";
import { useSelectTheme } from "../../store";
import {
  ButtonReverse,
  Checkbox,
  DragHandle,
  DropdownMenu,
  QuestionnaireInput,
  QuestionnaireInputInitialValue,
  QuestionnaireInputSize,
  QuestionnaireMediaDropdown,
  QuestionnaireOption,
  QuestionnaireOtherOption,
  RequiredTag,
  Select,
  SlideCheckbox,
  Tooltip,
} from "..";
import {
  QuestionnaireTempInputConfigOptions,
  QuestionnaireTempQuestion,
  QuestionnaireTempSection,
  TempQuestionnaireDisplayStrategyUpsertItem,
  UpsertDisplayRule,
} from "../../model";
import {
  CalendarGreyIcon,
  CheckGreyIcon,
  DateQuestionIcon,
  DateTimeQuestionIcon,
  MultiChoiceIcon,
  MultiSelectionIcon,
  NewCloseIcon,
  NumberQuestionIcon,
  OpenQuestionIcon,
  SliderQuestionIcon,
  TimeIcon,
  TimeQuestionIcon,
} from "../../asset/image";
import {
  IQuestionnaireDisplayLogicType,
  IQuestionnaireDisplayRule,
  IQuestionnaireDisplayRuleConditionNumber,
  IQuestionnaireDisplayRuleConditionOption,
  IQuestionnaireDisplayRuleConditionType,
  IQuestionnaireDisplayRuleSourceQuestion,
  IQuestionnaireDisplayRuleTargetQuestion,
  IQuestionnaireDisplayRuleTargetSection,
  IQuestionnaireDisplayRuleTargetSubmit,
  IQuestionnaireDisplayRuleTargetType,
  IQuestionnaireQuestion,
  IQuestionnaireSection,
  QuestionnaireQuestionType,
} from "../../shared/db";
import { components, OptionProps } from "react-select";
import { CSS } from "@dnd-kit/utilities";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { DropdownIcon } from "../../lib/svg/icon/dropdown";
import { SkipIcon } from "../../lib/svg/icon/skip";
import { InvisibleIcon } from "../../lib/svg/icon/invisible";
import { EyeIcon } from "../../lib/svg/icon/eye";
import {
  QuestionnaireSliderPreview,
  QuestionnaireSliderPreviewProps,
} from "./questionnaire_slider_preview";

type QuestionTypeOptionValue = QuestionnaireQuestionType | "slider_option" | "slider_range";

const SliderOptionTypeValue: QuestionTypeOptionValue = "slider_option";
const SliderRangeTypeValue: QuestionTypeOptionValue = "slider_range";

export const QuestionnaireQuestion = (props: {
  id: string;
  index: number;
  sectionId: string;
  question: QuestionnaireTempQuestion;
  tempSectionList: (QuestionnaireTempSection | IQuestionnaireSection)[];
  setTempSectionList: Dispatch<
    SetStateAction<(QuestionnaireTempSection | IQuestionnaireSection)[]>
  >;
  questionList: QuestionnaireTempQuestion[];
  editingBlock: string | null;
  setEditingBlock: Dispatch<SetStateAction<string | null>>;
  viewOnly: boolean;
  addQuestion: (sectionId: string, index?: number) => void;
  setMovingQuestion: Dispatch<SetStateAction<QuestionnaireTempQuestion | null>>;
  handleEditLogic: (targetType?: "section" | "question", targetId?: string) => void;
  upsertDisplayStrategyList: TempQuestionnaireDisplayStrategyUpsertItem[];
  setRenderPopupWarningDisplayStrategyForbiddenOperation: Dispatch<SetStateAction<boolean>>;
}) => {
  const { color, fontWeight, fontSize } = useSelectTheme();
  const {
    id,
    index,
    sectionId,
    question,
    tempSectionList,
    setTempSectionList,
    questionList,
    editingBlock,
    setEditingBlock,
    viewOnly,
    addQuestion,
    setMovingQuestion,
    handleEditLogic,
    upsertDisplayStrategyList,
    setRenderPopupWarningDisplayStrategyForbiddenOperation,
  } = props;
  const isEditing = editingBlock === id;
  const sensor = useSensor(PointerSensor);
  const sensors = useSensors(sensor);
  const { attributes, listeners, setNodeRef, transform, setActivatorNodeRef } = useSortable({
    id: props.id,
  });
  const style = { transform: CSS.Translate.toString(transform) };
  const hoveringRef = useRef(false);
  const [dropped, setDropped] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(dropdownRef, () => {
    setDropped(false);
  });
  const questionIndex = index + 1;
  const currentDisplayStrategy = upsertDisplayStrategyList.find((item) => item.questionId === id);
  const hasDisplayStrategy = useMemo(() => {
    if (!currentDisplayStrategy) return false;
    return currentDisplayStrategy.displayStrategy.displayLogicList?.some(
      ({ displayRuleList, type }) =>
        type === IQuestionnaireDisplayLogicType.Display &&
        displayRuleList &&
        displayRuleList.length > 0,
    );
  }, [currentDisplayStrategy]);

  const hasSkipLogic = useMemo(() => {
    if (!currentDisplayStrategy) return false;
    return currentDisplayStrategy.displayStrategy.displayLogicList?.some(
      ({ displayRuleList, type }) =>
        type === IQuestionnaireDisplayLogicType.Skip &&
        displayRuleList &&
        displayRuleList.length > 0,
    );
  }, [currentDisplayStrategy]);

  const lockedByDisplayStrategy = useMemo(() => {
    if (hasDisplayStrategy || hasSkipLogic) return true;
    return upsertDisplayStrategyList.some(({ displayStrategy }) => {
      return displayStrategy.displayLogicList?.some(({ displayRuleList }) => {
        return displayRuleList?.some((rule) => {
          const source = rule.source as IQuestionnaireDisplayRuleSourceQuestion;
          const target = rule.target as
            | IQuestionnaireDisplayRuleTargetSection
            | IQuestionnaireDisplayRuleTargetQuestion
            | IQuestionnaireDisplayRuleTargetSubmit;
          if (!target && !source) return false;
          return (
            (target?.type === IQuestionnaireDisplayRuleTargetType.Question &&
              target?.questionnaireQuestionId === id) ||
            source?.questionnaireQuestionId === id
          );
        });
      });
    });
  }, [upsertDisplayStrategyList]);

  const disableTypeChanging = useMemo(() => {
    if (hasDisplayStrategy || hasSkipLogic) return true;
    return upsertDisplayStrategyList.some(({ displayStrategy }) => {
      return displayStrategy.displayLogicList?.some(({ displayRuleList }) => {
        return displayRuleList?.some((rule) => {
          const source = rule.source as IQuestionnaireDisplayRuleSourceQuestion;
          if (!source) return false;
          return source.questionnaireQuestionId === id;
        });
      });
    });
  }, [upsertDisplayStrategyList]);

  const lockedOptionIds = useMemo(() => {
    if (question.type !== QuestionnaireQuestionType.Radio) {
      return new Set<string>();
    }
    const logicList = currentDisplayStrategy?.displayStrategy.displayLogicList ?? [];
    const optionIds = logicList
      .flatMap((logic) => logic.displayRuleList ?? [])
      .map((rule) => rule.condition?.inputOptionList?.[0]?.optionId)
      .filter((id): id is string => Boolean(id));

    return new Set(optionIds);
  }, [currentDisplayStrategy, question.type]);

  const isOptionLockedByDisplayStrategy = (value: string) => {
    return lockedOptionIds.has(value);
  };

  const isSliderOptionQuestion =
    question.type === QuestionnaireQuestionType.Radio && question.inputConfig?.showSlider === true;
  const isSliderRangeQuestion =
    question.type === QuestionnaireQuestionType.Number && question.inputConfig?.showSlider === true;
  const minimumSelectionOptionCount = isSliderOptionQuestion ? 2 : 1;

  const getPreviewText = (value: string | null | undefined, fallback: string) => {
    const trimmedValue = value?.trim();
    return trimmedValue && trimmedValue !== QuestionnaireInputInitialValue.QuestionLabel
      ? trimmedValue
      : fallback;
  };

  const QuestionTypeOption = (props: { label: string; imageUrl: string }) => {
    const { color } = useSelectTheme();
    const { label, imageUrl } = props;
    return (
      <div
        css={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <img src={imageUrl} width={24} height={24} />
        <span css={{ color: color.textIcon.secondary }}>{label}</span>
      </div>
    );
  };

  const getIconForType = (type: QuestionTypeOptionValue): string => {
    const icons: Record<QuestionTypeOptionValue, string> = {
      [QuestionnaireQuestionType.MultiSelect]: MultiSelectionIcon,
      [QuestionnaireQuestionType.Radio]: MultiChoiceIcon,
      [QuestionnaireQuestionType.Text]: OpenQuestionIcon,
      [QuestionnaireQuestionType.Time]: TimeQuestionIcon,
      [QuestionnaireQuestionType.Date]: DateQuestionIcon,
      [QuestionnaireQuestionType.DateTime]: DateTimeQuestionIcon,
      [QuestionnaireQuestionType.Number]: NumberQuestionIcon,
      [QuestionnaireQuestionType.Textarea]: NumberQuestionIcon,
      [QuestionnaireQuestionType.Checkbox]: NumberQuestionIcon,
      [QuestionnaireQuestionType.Select]: NumberQuestionIcon,
      [SliderOptionTypeValue]: SliderQuestionIcon,
      [SliderRangeTypeValue]: SliderQuestionIcon,
    };
    return icons[type];
  };

  type QuestionTypeOptionItem = {
    value: QuestionTypeOptionValue;
    label: JSX.Element;
  };

  const questionTypeMeta: { value: QuestionTypeOptionValue; label: string }[] = [
    { value: QuestionnaireQuestionType.MultiSelect, label: "Checkboxes (Choose Multiple)" },
    { value: QuestionnaireQuestionType.Radio, label: "Radio Buttons (Choose One)" },
    { value: QuestionnaireQuestionType.Number, label: "Number" },
    { value: SliderOptionTypeValue, label: "Slider (Option)" },
    { value: SliderRangeTypeValue, label: "Slider (Range)" },
    { value: QuestionnaireQuestionType.Text, label: "Open Answer" },
    { value: QuestionnaireQuestionType.Time, label: "Time" },
    { value: QuestionnaireQuestionType.Date, label: "Date" },
    { value: QuestionnaireQuestionType.DateTime, label: "Date and Time" },
  ];

  const questionTypeOptions: QuestionTypeOptionItem[] = questionTypeMeta.map(
    ({ value, label }) => ({
      value,
      label: <QuestionTypeOption label={label} imageUrl={getIconForType(value)} />,
    }),
  );

  const currentQuestionTypeValue: QuestionTypeOptionValue = isSliderOptionQuestion
    ? SliderOptionTypeValue
    : isSliderRangeQuestion
      ? SliderRangeTypeValue
      : question.type;

  const selectedQuestionTypeOption =
    questionTypeOptions.find((option) => option.value === currentQuestionTypeValue) ??
    questionTypeOptions.find((option) => option.value === question.type) ??
    questionTypeOptions[0];

  const SelectOption = (props: OptionProps<QuestionTypeOptionItem>) => {
    return (
      <div
        css={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: props.isSelected ? color.surface.default : color.white,
          ":hover": {
            backgroundColor: color.surface.default,
          },
          paddingLeft: "8px",
          paddingRight: "16px",
        }}
      >
        <components.Option
          {...props}
          css={{
            backgroundColor: "transparent",
          }}
        />
        {props.isSelected && <img src={CheckGreyIcon} width={16} />}
      </div>
    );
  };

  const updateTempSectionList = (newQuestion: QuestionnaireTempQuestion) => {
    const newTempSectionList = [...tempSectionList];
    const sectionIndex = newTempSectionList.findIndex((s) => s.id === sectionId);
    if (sectionIndex === -1) return;
    const section = newTempSectionList[sectionIndex];
    section.questionList?.splice(index, 1, newQuestion);
    setTempSectionList(newTempSectionList);
  };

  const handleDeleteQuestion = () => {
    if (viewOnly) return;
    if (lockedByDisplayStrategy)
      return setRenderPopupWarningDisplayStrategyForbiddenOperation(true);
    const newTempSectionList = [...tempSectionList];
    const sectionIndex = newTempSectionList.findIndex((s) => s.id === sectionId);
    if (sectionIndex === -1) return;
    newTempSectionList[sectionIndex].questionList = questionList.filter((q) => q.id !== id);
    setTempSectionList(newTempSectionList);
  };

  const handleMoveQuestion = () => {
    if (lockedByDisplayStrategy)
      return setRenderPopupWarningDisplayStrategyForbiddenOperation(true);
    setDropped(false);
    setMovingQuestion(question);
  };

  const handleDuplicateQuestion = () => {
    const newTempSectionList = [...tempSectionList];
    const sectionIndex = newTempSectionList.findIndex((s) => s.id === sectionId);
    if (sectionIndex === -1) return;
    const section = newTempSectionList[sectionIndex];
    const newQuestion = _.cloneDeep(question);
    newQuestion.id = t.uuid();
    section.questionList?.splice(index + 1, 0, newQuestion);
    setTempSectionList(newTempSectionList);
    setEditingBlock(newQuestion.id);
    setDropped(false);
  };

  const addOption = () => {
    const inputConfig = question.inputConfig;
    const optionList = inputConfig?.options ?? [];
    const newQuestion = {
      ...question,
      inputConfig: {
        ...inputConfig,
        options: [...optionList, { name: "New Option", value: t.uuid() }],
      },
    };
    updateTempSectionList(newQuestion);
  };

  const editOptionName = (optionIndex: number, name: string) => {
    // const newQuestion = {...question}
    // if (newQuestion?.inputConfig?.options?.[optionIndex]) {
    //   newQuestion.inputConfig.options[optionIndex].name = name
    // }
    updateTempSectionList({
      ...question,
      inputConfig: {
        ...question.inputConfig,
        options: question.inputConfig?.options?.map((option, i) => {
          return i === optionIndex ? { ...option, name: name } : option;
        }),
      },
    });
  };

  const createNewOption = () => ({ value: t.uuid(), name: "New Option" });

  const ensureMinimumOptions = (
    optionList: {
      value: string;
      name: string;
    }[],
    minimumCount: number,
  ) => {
    const nextOptions = [...optionList];
    while (nextOptions.length < minimumCount) {
      nextOptions.push(createNewOption());
    }
    return nextOptions;
  };

  const deleteOption = (optionIndex: number) => {
    const optionList = question.inputConfig?.options ?? [];
    if (optionList.length <= minimumSelectionOptionCount) return;
    const option = question.inputConfig?.options?.[optionIndex];
    if (!option) return;
    if (isOptionLockedByDisplayStrategy(option.value))
      return setRenderPopupWarningDisplayStrategyForbiddenOperation(true);
    const newQuestion = { ...question };
    if (newQuestion?.inputConfig?.options) {
      newQuestion.inputConfig?.options.splice(optionIndex, 1);
    }
    updateTempSectionList(newQuestion);
  };

  const handleChangeQuestionType = (option: { value: QuestionTypeOptionValue } | null) => {
    if (!option) return;
    const { value } = option;
    const newQuestion = { ...question };

    if (value === SliderOptionTypeValue) {
      newQuestion.type = QuestionnaireQuestionType.Radio;
      newQuestion.inputConfig = {
        options: ensureMinimumOptions(question.inputConfig?.options ?? [], 2),
        allowOther: false,
        customOtherName: undefined,
        showSlider: true,
      };
      updateTempSectionList(newQuestion);
      return;
    }

    if (value === SliderRangeTypeValue) {
      newQuestion.type = QuestionnaireQuestionType.Number;
      newQuestion.inputConfig = {
        showSlider: true,
        integer: true,
        min: 1,
        max: 5,
      };
      updateTempSectionList(newQuestion);
      return;
    }

    newQuestion.type = value;
    if (
      [
        QuestionnaireQuestionType.Text,
        QuestionnaireQuestionType.Time,
        QuestionnaireQuestionType.Date,
        QuestionnaireQuestionType.DateTime,
      ].includes(value)
    ) {
      newQuestion.inputConfig = undefined;
    }
    if (value === QuestionnaireQuestionType.Number) {
      newQuestion.inputConfig = {
        showSlider: false,
        integer: false,
        max: undefined,
        min: undefined,
      };
    }
    if ([QuestionnaireQuestionType.MultiSelect, QuestionnaireQuestionType.Radio].includes(value)) {
      newQuestion.inputConfig = {
        options: ensureMinimumOptions(question.inputConfig?.options ?? [], 1),
        allowOther: question.inputConfig?.allowOther ?? false,
        customOtherName: question.inputConfig?.customOtherName,
        showSlider: false,
      };
    }
    updateTempSectionList(newQuestion);
  };

  const handleChangeEnableRange = (value: boolean) => {
    const newQuestion = { ...question, inputConfig: { ...question.inputConfig } };
    if (value) {
      newQuestion.inputConfig.min = 0;
      newQuestion.inputConfig.max = 100;
    } else {
      newQuestion.inputConfig.min = undefined;
      newQuestion.inputConfig.max = undefined;
    }
    updateTempSectionList(newQuestion);
  };

  const handleChangeInteger = (value: boolean) => {
    const newQuestion = { ...question, inputConfig: { ...question.inputConfig } };
    newQuestion.inputConfig.min = undefined;
    newQuestion.inputConfig.max = undefined;
    newQuestion.inputConfig.integer = value;
    updateTempSectionList(newQuestion);
  };

  const handleChangeNumberMax = (value: string) => {
    const newQuestion = { ...question, inputConfig: { ...question.inputConfig } };
    if (value === "") {
      newQuestion.inputConfig.max = 0;
    } else {
      const number = parseInt(value);
      if (!isNaN(number)) {
        newQuestion.inputConfig.max = parseInt(value);
      }
    }
    updateTempSectionList(newQuestion);
  };

  const handleChangeNumberMin = (value: string) => {
    const newQuestion = { ...question, inputConfig: { ...question.inputConfig } };
    if (value === "") {
      newQuestion.inputConfig.min = 0;
    } else {
      const number = parseInt(value);
      if (!isNaN(number)) {
        newQuestion.inputConfig.min = parseInt(value);
      }
    }
    updateTempSectionList(newQuestion);
  };

  const handleChangeNumberMinLabel = (value: string) => {
    const newQuestion = { ...question, inputConfig: { ...question.inputConfig } };
    newQuestion.inputConfig.minLabel = value;
    updateTempSectionList(newQuestion);
  };

  const handleChangeNumberMaxLabel = (value: string) => {
    const newQuestion = { ...question, inputConfig: { ...question.inputConfig } };
    newQuestion.inputConfig.maxLabel = value;
    updateTempSectionList(newQuestion);
  };

  const handleChangeQuestionDescription = (value: string) => {
    const newQuestion = { ...question, description: value };
    updateTempSectionList(newQuestion);
  };

  const handleChangeRequiredStatus = (e: boolean) => {
    const newQuestion = { ...question, required: e };
    updateTempSectionList(newQuestion);
  };

  const handleChangeOfAllowOtherStatus = (e: boolean) => {
    const newQuestion = { ...question, inputConfig: { ...question.inputConfig, allowOther: e } };
    updateTempSectionList(newQuestion);
  };

  const handleChangeOfCustomOtherName = (value: string) => {
    const newQuestion = {
      ...question,
      inputConfig: { ...question.inputConfig, customOtherName: value },
    };
    updateTempSectionList(newQuestion);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (viewOnly) return;
    e.stopPropagation();
    setEditingBlock(id);
  };

  const handleClickAddNewQuestion = (e: React.MouseEvent) => {
    e.stopPropagation();
    addQuestion(sectionId, index);
  };

  const handleMouseEnter = () => {
    hoveringRef.current = true;
    // setHovering(true)
  };

  const handleMouseLeave = () => {
    hoveringRef.current = false;
    // setHovering(false)
  };

  const handleDropdownClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setDropped(!dropped);
  };

  const removeMediaUrl = () => {
    const newQuestion = { ...question, mediaUrl: null };
    updateTempSectionList(newQuestion);
  };

  const sanitizedMediaUrl = () => {
    const mediaUrl = question.mediaUrl;
    if (!mediaUrl) return "";
    const videoId = extractVideoId(mediaUrl);
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    }
    return mediaUrl;
  };

  const extractVideoId = (url?: string | null) => {
    const regex = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url?.match(regex);
    return match ? match[1] : "";
  };
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) return;
    if (active.id === over.id) return;
    // if (isOptionLockedByDisplayStrategy(active.id as string))
    //   return setRenderPopupWarningDisplayStrategyForbiddenOperation(true)
    const newQuestion = { ...question };
    const options = newQuestion.inputConfig?.options ?? [];
    const oldIndex = options.findIndex((i) => i.value === active.id);
    const newIndex = options.findIndex((i) => i.value === over.id);
    updateTempSectionList({
      ...newQuestion,
      inputConfig: { ...newQuestion.inputConfig, options: arrayMove(options, oldIndex, newIndex) },
    });
  };

  const showDragHandle = useMemo(() => {
    if (viewOnly) return false;
    return isEditing || hoveringRef.current;
  }, [viewOnly, isEditing, hoveringRef.current]);

  const contentList = useMemo(() => {
    return tempSectionList.flatMap((section) => [section, ...(section.questionList ?? [])]);
  }, [tempSectionList]);

  const conditionTranslator = {
    "=": "equals",
    "<=": "less than or equal to",
    ">=": "greater than or equal to",
    "<": "less than",
    ">": "greater than",
  };

  const generateRuleText = (rule: UpsertDisplayRule, type: IQuestionnaireDisplayLogicType) => {
    const ruleSource = rule.source as IQuestionnaireDisplayRuleSourceQuestion;
    const ruleCondition = rule.condition as
      | IQuestionnaireDisplayRuleConditionNumber
      | IQuestionnaireDisplayRuleConditionOption;
    const ruleTarget = rule.target as
      | IQuestionnaireDisplayRuleTargetQuestion
      | IQuestionnaireDisplayRuleTargetSection
      | IQuestionnaireDisplayRuleTargetSubmit;
    switch (type) {
      case IQuestionnaireDisplayLogicType.Skip: {
        if (ruleTarget.type === IQuestionnaireDisplayRuleTargetType.Submit) {
          if (question.type === QuestionnaireQuestionType.Number) {
            if (ruleCondition.inputNumberList && ruleCondition.inputNumberList.length > 1) {
              const numberMin = Math.min(
                ruleCondition.inputNumberList[0].value,
                ruleCondition.inputNumberList[1].value,
              );
              const numberMax = Math.max(
                ruleCondition.inputNumberList[0].value,
                ruleCondition.inputNumberList[1].value,
              );
              return `If value between ${numberMin} and ${numberMax}, Submit the questionnaire`;
            }
            return `If ${conditionTranslator[ruleCondition.inputNumberList?.[0].operator ?? "="]} ${
              ruleCondition.inputNumberList?.[0].value
            }, Submit the questionnaire`;
          }
          return "Submit the questionnaire";
        }
        // if (ruleTarget.questionnaireQuestionId === 'next') {
        //   if (question.type === QuestionnaireQuestionType.Number) {
        //     if (ruleCondition.inputNumberList && ruleCondition.inputNumberList.length > 1) {
        //       const numberMin = Math.min(ruleCondition.inputNumberList[0].value, ruleCondition.inputNumberList[1].value)
        //       const numberMax = Math.max(ruleCondition.inputNumberList[0].value, ruleCondition.inputNumberList[1].value)
        //       return `If value between ${numberMin} and ${numberMax}, Skip to the next question`
        //     }
        //     return `If ${conditionTranslator[ruleCondition.inputNumberList?.[0].operator ?? '=']} ${
        //       ruleCondition.inputNumberList?.[0].value
        //     }, Skip to the next question`
        //   }
        // }
        const targetItem = contentList.find(({ id }) =>
          ruleTarget.type === IQuestionnaireDisplayRuleTargetType.Section
            ? id === ruleTarget.questionnaireSectionId
            : id === ruleTarget.questionnaireQuestionId,
        );
        if (!targetItem) return "";
        let sectionIndex = 0;
        let questionIndex = 0;
        if ("legend" in targetItem) {
          sectionIndex = (targetItem.index ?? 0) + 1;
        } else {
          for (let i = 0; i < contentList.length; i++) {
            if ("legend" in contentList[i]) {
              questionIndex = 0;
              sectionIndex += 1;
            } else {
              questionIndex += 1;
            }
            if (contentList[i].id === targetItem.id) {
              break;
            }
          }
        }
        if (question.type === QuestionnaireQuestionType.Number) {
          if (ruleCondition.inputNumberList && ruleCondition.inputNumberList.length > 1) {
            const numberMin = Math.min(
              ruleCondition.inputNumberList[0].value,
              ruleCondition.inputNumberList[1].value,
            );
            const numberMax = Math.max(
              ruleCondition.inputNumberList[0].value,
              ruleCondition.inputNumberList[1].value,
            );
            if ("legend" in targetItem) {
              return `If value between ${numberMin} and ${numberMax}, Skip to S${sectionIndex}: ${
                targetItem.legend.length > 0 ? targetItem.legend : "Untitled Section"
              }`;
            }
            return `If value between ${numberMin} and ${numberMax}, Skip to S${sectionIndex}-Q${questionIndex}. ${targetItem.description}`;
          }
          if ("legend" in targetItem) {
            return `If ${conditionTranslator[ruleCondition.inputNumberList?.[0].operator ?? "="]} ${
              ruleCondition.inputNumberList?.[0].value
            }, Skip to S${sectionIndex}: ${targetItem.legend.length > 0 ? targetItem.legend : "Untitled Section"}`;
          } else {
            return `If ${conditionTranslator[ruleCondition.inputNumberList?.[0].operator ?? "="]} ${
              ruleCondition.inputNumberList?.[0].value
            }, Skip to S${sectionIndex}-Q${questionIndex}. ${targetItem.description}`;
          }
        } else {
          if ("legend" in targetItem) {
            return `Skip to S${sectionIndex}: ${targetItem.legend.length > 0 ? targetItem.legend : "Untitled Section"}`;
          } else {
            return `Skip to S${sectionIndex}-Q${questionIndex}. ${targetItem.description ?? ""}`;
          }
        }
      }
      case IQuestionnaireDisplayLogicType.Display: {
        const sourceQuestion = contentList.find(
          ({ id }) => id === ruleSource.questionnaireQuestionId,
        );
        if (!sourceQuestion) return "";
        let sectionIndex = 0;
        let questionIndex = 0;
        for (let i = 0; i < contentList.length; i++) {
          if ("legend" in contentList[i]) {
            questionIndex = 0;
            sectionIndex += 1;
          } else {
            questionIndex += 1;
          }
          if (contentList[i].id === sourceQuestion.id) {
            break;
          }
        }
        const typedSourceQuestion = sourceQuestion as
          | QuestionnaireTempQuestion
          | IQuestionnaireQuestion;
        if (ruleCondition.type === IQuestionnaireDisplayRuleConditionType.Option) {
          const targetOptionId = ruleCondition.inputOptionList?.[0]?.optionId;
          const optionName =
            (targetOptionId &&
              (
                typedSourceQuestion.inputConfig as QuestionnaireTempInputConfigOptions
              ).options?.find(({ value, id }) => value === targetOptionId || id === targetOptionId)
                ?.name) ||
            "";
          return `Hide this question unless S${sectionIndex}-Q${questionIndex}. ${typedSourceQuestion.description} [${optionName}] is selected`;
        }
        if (ruleCondition.inputNumberList && ruleCondition.inputNumberList.length > 1) {
          const numberMin = Math.min(
            ruleCondition.inputNumberList[0].value,
            ruleCondition.inputNumberList[1].value,
          );
          const numberMax = Math.max(
            ruleCondition.inputNumberList[0].value,
            ruleCondition.inputNumberList[1].value,
          );
          return `Hide this question unless S${sectionIndex}-Q${questionIndex}. ${typedSourceQuestion.description} value between ${numberMin} and ${numberMax}`;
        }
        return `Hide this question unless S${sectionIndex}-Q${questionIndex}. ${
          typedSourceQuestion.description
        } value ${ruleCondition.inputNumberList?.[0].operator ?? "="} ${ruleCondition.inputNumberList?.[0].value ?? 0}`;
      }
      default:
        return "";
    }
  };

  const getSkipLogicText = (optionId: string) => {
    const matchingRule = upsertDisplayStrategyList
      .find((item) => item.questionId === id)
      ?.displayStrategy.displayLogicList?.filter(
        (logic) => logic.type === IQuestionnaireDisplayLogicType.Skip,
      )
      .flatMap((logic) => logic.displayRuleList ?? [])
      .find(
        (rule) =>
          (rule?.condition as IQuestionnaireDisplayRuleConditionOption)?.inputOptionList?.[0]
            ?.optionId === optionId,
      );

    return matchingRule
      ? generateRuleText(matchingRule, IQuestionnaireDisplayLogicType.Skip)
      : undefined;
  };

  // generate a list that contains id of section/question that has display logic referring to this question
  const getDisplayLogicText = (optionId: string) => {
    const hasMatchingOption = (ruleList: (UpsertDisplayRule | undefined)[]) =>
      ruleList.some(
        (rule) =>
          (rule?.condition as IQuestionnaireDisplayRuleConditionOption)?.inputOptionList?.[0]
            ?.optionId === optionId,
      );

    return upsertDisplayStrategyList
      .filter(({ displayStrategy }) => {
        const ruleList =
          displayStrategy.displayLogicList
            ?.filter((logic) => logic.type === IQuestionnaireDisplayLogicType.Display)
            .flatMap(({ displayRuleList }) => displayRuleList ?? []) ?? [];
        return hasMatchingOption(ruleList);
      })
      .map(({ sectionId, questionId }) => {
        if (sectionId) {
          const targetSection = tempSectionList.find(({ id }) => id === sectionId);
          if (!targetSection) return null;
          return `Display S${(targetSection.index ?? 0) + 1}: ${targetSection.legend || "Untitled Section"}`;
        }
        const targetQuestion = contentList.find(({ id }) => id === questionId);
        if (!targetQuestion || "legend" in targetQuestion) return null;
        let sectionIndex = 0;
        let questionIndex = 0;
        for (let i = 0; i < contentList.length; i++) {
          if ("legend" in contentList[i]) {
            questionIndex = 0;
            sectionIndex += 1;
          } else {
            questionIndex += 1;
          }
          if (contentList[i].id === targetQuestion.id) break;
        }
        return `Display S${sectionIndex}-Q${questionIndex}. ${targetQuestion.description || "Untitled Question"}`;
      })
      .filter((text): text is string => text !== null);
  };

  // generate text for number question that has display logic referring to this question
  const formatLogicText = (
    rules: IQuestionnaireDisplayRule[],
    prefixLabel: string,
    suffixLabel: string,
  ): string[] => {
    const texts: string[] = [];

    rules.forEach((rule) => {
      const condition = rule?.condition as IQuestionnaireDisplayRuleConditionNumber;
      const inputList = condition?.inputNumberList;

      if (!inputList || inputList.length === 0) return;

      if (inputList.length > 1) {
        const min = Math.min(inputList[0].value, inputList[1].value);
        const max = Math.max(inputList[0].value, inputList[1].value);
        texts.push(`If value between ${min} and ${max}, Display ${prefixLabel}: ${suffixLabel}`);
      } else {
        const { operator, value } = inputList[0];
        texts.push(`If value ${operator} ${value}, Display ${prefixLabel}: ${suffixLabel}`);
      }
    });

    return texts;
  };

  const numberDisplayLogicTextList = useMemo(() => {
    const sectionMap = new Map<string, { index: number; legend: string }>();
    tempSectionList.forEach((s) => {
      sectionMap.set(s.id, {
        index: (s.index ?? 0) + 1,
        legend: s.legend || "Untitled Section",
      });
    });

    const questionMap = new Map<string, { sIndex: number; qIndex: number; description: string }>();
    let currentSecIdx = 0;
    let currentQueIdx = 0;

    contentList.forEach((item) => {
      if ("legend" in item) {
        currentSecIdx += 1;
        currentQueIdx = 0;
      } else {
        currentQueIdx += 1;
        questionMap.set(item.id, {
          sIndex: currentSecIdx,
          qIndex: currentQueIdx,
          description: item.description || "Untitled Question",
        });
      }
    });

    const result: string[] = [];

    upsertDisplayStrategyList.forEach(({ displayStrategy, sectionId, questionId }) => {
      const validRules =
        displayStrategy.displayLogicList
          ?.filter((logic) => logic.type === IQuestionnaireDisplayLogicType.Display)
          .flatMap((logic) => logic.displayRuleList)
          .filter(
            (rule): rule is IQuestionnaireDisplayRule =>
              !!rule?.condition?.inputNumberList?.length &&
              rule?.source?.questionnaireQuestionId === id,
          ) ?? [];

      if (validRules.length === 0) return;

      if (sectionId) {
        const sectionData = sectionMap.get(sectionId);
        if (sectionData) {
          const formattedTexts = formatLogicText(
            validRules,
            `S${sectionData.index}`,
            sectionData.legend,
          );
          result.push(...formattedTexts);
        }
      } else if (questionId) {
        const questionData = questionMap.get(questionId);
        if (questionData) {
          const formattedTexts = formatLogicText(
            validRules,
            `S${questionData.sIndex}-Q${questionData.qIndex}`,
            questionData.description,
          );
          result.push(...formattedTexts);
        }
      }
    });

    return result;
  }, [upsertDisplayStrategyList, contentList, tempSectionList]);

  const questionPreviewText = getPreviewText(question.description, "Please enter a question");
  const sliderOptionList = question.inputConfig?.options ?? [];
  const sliderOptionPreviewOptionCount = Math.max(
    sliderOptionList.length,
    minimumSelectionOptionCount,
  );
  const sliderOptionPreviewSelectedValue = Math.round((1 + sliderOptionPreviewOptionCount) / 2);
  const sliderOptionPreviewSelectedOption = sliderOptionList[sliderOptionPreviewSelectedValue - 1];
  const sliderOptionPreviewData: QuestionnaireSliderPreviewProps = {
    questionText: questionPreviewText,
    minValue: 1,
    maxValue: sliderOptionPreviewOptionCount,
    selectedValue: sliderOptionPreviewSelectedValue,
    minLabel: getPreviewText(sliderOptionList[0]?.name, "Option 1"),
    maxLabel: getPreviewText(
      sliderOptionList[sliderOptionList.length - 1]?.name,
      `Option ${sliderOptionPreviewOptionCount}`,
    ),
    selectedOptionLabel: getPreviewText(
      sliderOptionPreviewSelectedOption?.name,
      `Option ${sliderOptionPreviewSelectedValue}`,
    ),
    showAnswer: true,
  };
  const sliderRangePreviewMinValue = question.inputConfig?.min ?? 1;
  const sliderRangePreviewMaxValue = question.inputConfig?.max ?? 5;
  const sliderRangePreviewData: QuestionnaireSliderPreviewProps = {
    questionText: questionPreviewText,
    minValue: sliderRangePreviewMinValue,
    maxValue: sliderRangePreviewMaxValue,
    selectedValue: Math.round((sliderRangePreviewMinValue + sliderRangePreviewMaxValue) / 2),
    minLabel: getPreviewText(question.inputConfig?.minLabel, "Left side label"),
    maxLabel: getPreviewText(question.inputConfig?.maxLabel, "Right side label"),
  };

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={setNodeRef}
      {...attributes}
      style={{
        ...style,
        marginLeft: "-24px",
        paddingLeft: "24px",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-end",
        cursor: viewOnly ? "" : "pointer",
      }}
    >
      {/* drag handle */}
      {showDragHandle && <DragHandle ref={setActivatorNodeRef} {...listeners} />}
      <div
        style={{
          width: "calc(100% - 24px)",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <div
          onClick={handleClick}
          css={{
            width: "100%",
            borderRadius: "5px",
            border: isEditing ? `1px solid ${color.border.blue}` : "none",
            background: color.white,
            position: "relative",
            // cursor: viewOnly ? 'auto' : 'pointer',
            boxShadow: "0px 4px 12px rgba(213, 213, 213, 0.25)",
            ":hover": {
              background: viewOnly ? color.white : color.grey_20,
            },
          }}
        >
          {/* show dropdown menu when hovered */}
          {/* {hoveringRef.current && (
            <div
              css={{
                position: 'absolute',
                top: '16px',
                right: '12px',
              }}
            >
              <QuestionnaireDropdown
                {...{
                  index,
                  deleteItem,
                  duplicateItem,
                }}
              />
            </div>
          )} */}
          {/* type selection, switch for required state and dropdown menu */}
          {isEditing && (
            <div
              css={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px",
                backgroundColor: color.surface.default,
                borderRadius: "5px 5px 0 0",
              }}
            >
              <div
                onClick={() => {
                  if (disableTypeChanging)
                    setRenderPopupWarningDisplayStrategyForbiddenOperation(true);
                }}
              >
                <Select
                  components={{ Option: SelectOption }}
                  options={questionTypeOptions}
                  value={selectedQuestionTypeOption}
                  style={{
                    option: (base: CSSProperties) => ({
                      ...base,
                      fontSize: fontSize.h7,
                      color: color.textIcon.title,
                    }),
                  }}
                  onChange={handleChangeQuestionType}
                  isDisabled={disableTypeChanging}
                  css={{
                    height: "40px",
                    width: "290px",
                  }}
                />
              </div>
              <div
                css={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "16px",
                }}
              >
                {/* edit logic button */}
                {!viewOnly && (
                  <button
                    onClick={() => handleEditLogic("question", question.id)}
                    css={{
                      padding: "3px 6px",
                      border: `1px solid ${color.border.default}`,
                      borderRadius: "3px",
                      color: color.textIcon.secondary,
                      backgroundColor: "transparent",
                      cursor: "pointer",
                      fontSize: fontSize.h7,
                      fontWeight: fontWeight.bold,
                    }}
                  >
                    Edit logic
                  </button>
                )}
                {/* switch for required state */}
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <SlideCheckbox
                    value={question?.required || false}
                    onChange={handleChangeRequiredStatus}
                  />
                  <span css={{ fontWeight: fontWeight.bold, color: color.textIcon.secondary }}>
                    Required
                  </span>
                </div>
                {/* dropdown menu */}
                <div
                  ref={dropdownRef}
                  css={{
                    position: "relative",
                  }}
                >
                  <button
                    onClick={handleDropdownClick}
                    css={{
                      height: "32px",
                      width: "32px",
                      backgroundColor: "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "none",
                      cursor: "pointer",
                      ":hover": { backgroundColor: color.surface.hover.hoverWhiteBg },
                    }}
                  >
                    <DropdownIcon
                      {...{
                        width: 24,
                        height: 24,
                      }}
                    />
                  </button>
                  <DropdownMenu
                    {...{
                      dropped,
                      options: [
                        { text: "Move Question to Another Section", onClick: handleMoveQuestion },
                        { text: "Duplicate (w/o Logic)", onClick: handleDuplicateQuestion },
                        {
                          text: "Delete",
                          textColor: color.textIcon.warning,
                          onClick: handleDeleteQuestion,
                        },
                      ],
                      parentCSS: { top: "32px", right: 0 },
                    }}
                  />
                </div>
                {/* <QuestionnaireDropdown
                  {...{
                    index,
                    deleteItem,
                    duplicateItem,
                  }}
                /> */}
              </div>
            </div>
          )}
          {/* display logic */}
          {hasDisplayStrategy && (
            <div
              css={{
                width: "100%",
                padding: isEditing ? "16px" : "8px",
                borderBottom: isEditing ? `1px dashed ${color.border.black}` : "none",
                display: "flex",
                flexDirection: "column",
                gap: isEditing ? "8px" : "4px",
                backgroundColor: isEditing ? "transparent" : color.surface.nested,
                borderTopLeftRadius: isEditing ? 0 : "5px",
                borderTopRightRadius: isEditing ? 0 : "5px",
              }}
            >
              {currentDisplayStrategy?.displayStrategy.displayLogicList
                ?.filter(({ type }) => type === IQuestionnaireDisplayLogicType.Display)
                .flatMap(({ type, displayRuleList }) =>
                  displayRuleList?.map((rule) => (
                    <div
                      key={t.uuid()}
                      css={{
                        width: "100%",
                        padding: "4px 16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        backgroundColor: color.surface.nested,
                      }}
                    >
                      <InvisibleIcon width={14} fill={color.textIcon.secondary} />
                      <span
                        css={{
                          color: color.textIcon.secondary,
                          fontSize: fontSize.h7,
                          fontWeight: fontWeight.bold,
                        }}
                      >
                        {generateRuleText(rule, type)}
                      </span>
                    </div>
                  )),
                )}
            </div>
          )}
          {/* {!isEditing && hasDisplayStrategy && (
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                padding: '8px',
                backgroundColor: color.surface.nested,
                borderTopLeftRadius: '5px',
                borderTopRightRadius: '5px',
              }}
            ></div>
          )} */}

          {/* question content */}
          <div
            css={{
              width: "100%",
              padding: "16px 24px 24px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            {/* question index & description when not editing */}
            {!isEditing && (
              <div
                css={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span
                  css={{
                    width: "20px",
                    maxWidth: "20px",
                    minWidth: "20px",
                  }}
                >
                  {questionIndex}.
                </span>
                <span
                  css={{
                    flex: "1 1 0",
                    minWidth: 0,
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  {question?.description}
                </span>
                {question?.required && <RequiredTag />}
              </div>
            )}
            {/* question index & description input & media dropdown when editing */}
            {isEditing && (
              <div css={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span css={{ width: "20px", minWidth: "20px" }}>{`${questionIndex}.`}</span>
                <QuestionnaireInput
                  draftField={`question:${id}:description`}
                  defaultValue={question?.description}
                  onChangeAction={handleChangeQuestionDescription}
                  placeholder={QuestionnaireInputInitialValue.QuestionLabel}
                  initialValue={QuestionnaireInputInitialValue.QuestionLabel}
                  maxLength={1500}
                  // size={QuestionnaireInputSize.extraSmall}
                />
                <QuestionnaireMediaDropdown
                  {...{
                    question,
                    updateTempSectionList,
                    // questionnaireContentList,
                    // setQuestionnaireContentList,
                    // addPendingMedia,
                    index,
                  }}
                />
              </div>
            )}
            {/* media */}
            {question.mediaUrl && (
              <div
                css={{
                  width: "120px",
                  height: "108px",
                  position: "relative",
                  marginLeft: "28px",
                  boxShadow: "0px 4px 10px 1px #68686840",
                  borderRadius: "5px",
                  padding: "4px",
                  backgroundColor: color.surface.white,
                }}
              >
                {isEditing && (
                  <button
                    onClick={removeMediaUrl}
                    css={{
                      width: "16px",
                      height: "16px",
                      borderRadius: "50%",
                      backgroundColor: color.black,
                      backgroundImage: `url(${NewCloseIcon})`,
                      backgroundSize: "12px",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                      position: "absolute",
                      top: "-4px",
                      right: "-4px",
                      border: "none",
                      cursor: "pointer",
                    }}
                  />
                )}
                <img width={112} height={100} src={sanitizedMediaUrl()} />
              </div>
            )}
            {/* content */}
            {/* question type: text */}
            {question?.type === QuestionnaireQuestionType.Text && (
              <p
                css={{
                  marginLeft: "28px",
                  padding: "11px 16px",
                  color: color.textIcon.hint,
                  borderBottom: `1px dashed ${color.border.default}`,
                }}
              >
                Participant's answer goes here
              </p>
            )}
            {/* question type: number */}
            {question?.type === QuestionnaireQuestionType.Number && !isSliderRangeQuestion && (
              <div
                css={{
                  marginLeft: "28px",
                }}
              >
                <div
                  css={{
                    height: "40px",
                    minHeight: "40px",
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "16px",
                    color: color.textIcon.hint,
                    borderBottom: `1px dashed ${color.border.default}`,
                  }}
                >
                  Participant's answer goes here
                </div>
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    gap: "64px",
                    marginTop: "24px",
                  }}
                >
                  {/* left part */}
                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Checkbox
                      value={question?.inputConfig?.integer ?? false}
                      onChange={handleChangeInteger}
                    />
                    <p css={{ color: color.textIcon.secondary, whiteSpace: "nowrap" }}>
                      The answer should be an integer
                    </p>
                    <Tooltip content="The answer should be a whole number (no decimals, can be negative)" />
                  </div>
                  {/* right part */}
                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                    }}
                  >
                    <div
                      css={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Checkbox
                        value={question?.inputConfig?.max !== undefined}
                        onChange={handleChangeEnableRange}
                      />
                      <p css={{ color: color.textIcon.secondary, whiteSpace: "nowrap" }}>
                        Range between
                      </p>
                    </div>
                    <QuestionnaireInput
                      draftField={`question:${id}:min`}
                      size={QuestionnaireInputSize.extraSmall}
                      defaultValue={(question?.inputConfig?.min ?? 0) + ""}
                      onChangeAction={handleChangeNumberMin}
                      placeholder={"0"}
                      initialValue={"0"}
                      disabled={question?.inputConfig?.min === undefined}
                      maxLength={250}
                      inputType={question?.inputConfig?.integer ? "integer" : "number"}
                      hideWordCount={true}
                    />
                    <p css={{ color: color.textIcon.secondary, fontWeight: fontWeight.bold }}>to</p>
                    <QuestionnaireInput
                      draftField={`question:${id}:max`}
                      size={QuestionnaireInputSize.extraSmall}
                      defaultValue={(question?.inputConfig?.max ?? 100) + ""}
                      onChangeAction={handleChangeNumberMax}
                      placeholder={"100"}
                      initialValue={"100"}
                      disabled={question?.inputConfig?.max === undefined}
                      inputType={question?.inputConfig?.integer ? "integer" : "number"}
                      maxLength={250}
                      hideWordCount={true}
                    />
                  </div>
                </div>
              </div>
            )}
            {/* question type: slider range */}
            {question?.type === QuestionnaireQuestionType.Number && isSliderRangeQuestion && (
              <div
                css={{
                  marginLeft: "28px",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "24px",
                }}
              >
                <div
                  css={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      maxWidth: "420px",
                    }}
                  >
                    <p
                      css={{
                        width: "104px",
                        minWidth: "104px",
                        color: color.textIcon.secondary,
                        fontWeight: fontWeight.medium,
                      }}
                    >
                      Left side label
                    </p>
                    <div
                      css={{
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <QuestionnaireInput
                        draftField={`question:${id}:min-label`}
                        defaultValue={question?.inputConfig?.minLabel ?? ""}
                        onChangeAction={handleChangeNumberMinLabel}
                        placeholder="Enter left side label"
                        initialValue=""
                        maxLength={100}
                        hideWordCount={true}
                      />
                    </div>
                  </div>

                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      maxWidth: "420px",
                    }}
                  >
                    <p
                      css={{
                        width: "104px",
                        minWidth: "104px",
                        color: color.textIcon.secondary,
                        fontWeight: fontWeight.medium,
                      }}
                    >
                      Right side label
                    </p>
                    <div
                      css={{
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <QuestionnaireInput
                        draftField={`question:${id}:max-label`}
                        defaultValue={question?.inputConfig?.maxLabel ?? ""}
                        onChangeAction={handleChangeNumberMaxLabel}
                        placeholder="Enter right side label"
                        initialValue=""
                        maxLength={100}
                        hideWordCount={true}
                      />
                    </div>
                  </div>

                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                    }}
                  >
                    <p
                      css={{
                        width: "104px",
                        minWidth: "104px",
                        color: color.textIcon.secondary,
                        fontWeight: fontWeight.medium,
                      }}
                    >
                      Scale Range
                    </p>
                    <QuestionnaireInput
                      draftField={`question:${id}:min`}
                      size={QuestionnaireInputSize.extraSmall}
                      defaultValue={(question?.inputConfig?.min ?? 1) + ""}
                      onChangeAction={handleChangeNumberMin}
                      placeholder={"1"}
                      initialValue={"1"}
                      inputType={"integer"}
                      maxLength={250}
                      hideWordCount={true}
                    />
                    <p css={{ color: color.textIcon.secondary, fontWeight: fontWeight.bold }}>to</p>
                    <QuestionnaireInput
                      draftField={`question:${id}:max`}
                      size={QuestionnaireInputSize.extraSmall}
                      defaultValue={(question?.inputConfig?.max ?? 5) + ""}
                      onChangeAction={handleChangeNumberMax}
                      placeholder={"5"}
                      initialValue={"5"}
                      inputType={"integer"}
                      maxLength={250}
                      hideWordCount={true}
                    />
                  </div>
                </div>

                <QuestionnaireSliderPreview {...sliderRangePreviewData} />
              </div>
            )}
            {/* question type: time */}
            {question?.type === QuestionnaireQuestionType.Time && (
              <div css={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "28px" }}>
                <img src={TimeIcon} width={20} />
                <p
                  css={{
                    color: color.textIcon.secondary,
                  }}
                >
                  Hour / Minutes
                </p>
              </div>
            )}
            {/* question type: date */}
            {question?.type === QuestionnaireQuestionType.Date && (
              <div css={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "28px" }}>
                <img src={CalendarGreyIcon} width={20} />
                <p
                  css={{
                    color: color.textIcon.secondary,
                  }}
                >
                  Month / Day / Year
                </p>
              </div>
            )}
            {/* question type: date and time */}
            {question?.type === QuestionnaireQuestionType.DateTime && (
              <div
                css={{
                  display: "flex",
                  gap: "40px",
                }}
              >
                <div
                  css={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "28px" }}
                >
                  <img src={CalendarGreyIcon} width={20} />
                  <p
                    css={{
                      color: color.textIcon.secondary,
                    }}
                  >
                    Month / Day / Year
                  </p>
                </div>
                <div css={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <img src={TimeIcon} width={20} />
                  <p
                    css={{
                      color: color.textIcon.secondary,
                    }}
                  >
                    Hour / Minutes
                  </p>
                </div>
              </div>
            )}
            {/* question type: multi select/choice */}
            {[QuestionnaireQuestionType.MultiSelect, QuestionnaireQuestionType.Radio].includes(
              question?.type,
            ) && (
              <div
                css={{
                  display: "flex",
                  flexDirection: isEditing && isSliderOptionQuestion ? "row" : "column",
                  alignItems: isEditing && isSliderOptionQuestion ? "flex-start" : "stretch",
                  gap: isEditing && isSliderOptionQuestion ? "24px" : isEditing ? "8px" : "16px",
                  marginLeft: isEditing ? 0 : "28px",
                }}
              >
                <div
                  css={{
                    display: "flex",
                    flexDirection: "column",
                    gap: isEditing ? "8px" : "16px",
                    flex: isEditing && isSliderOptionQuestion ? 1 : undefined,
                    minWidth: 0,
                  }}
                >
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <div
                      css={{
                        display: "flex",
                        flexDirection: "column",
                        gap: isEditing ? "8px" : "16px",
                      }}
                    >
                      <SortableContext
                        items={
                          question.inputConfig?.options?.map((option) => ({
                            ...option,
                            id: option.value,
                          })) ?? []
                        }
                        strategy={verticalListSortingStrategy}
                      >
                        {question?.inputConfig?.options?.map((option, i, a) => (
                          <QuestionnaireOption
                            {...{
                              key: option.value,
                              index: i,
                              isEditing,
                              question,
                              option,
                              editOptionName,
                              deleteOption,
                              showDeleteButton: a.length > minimumSelectionOptionCount,
                              viewOnly,
                              skipLogicText: getSkipLogicText(option.value),
                              displayLogicText: getDisplayLogicText(option.value),
                            }}
                          />
                        ))}
                      </SortableContext>
                    </div>
                  </DndContext>
                  {!isSliderOptionQuestion && question.inputConfig?.allowOther && (
                    <QuestionnaireOtherOption
                      {...{
                        isEditing,
                        question,
                      }}
                    />
                  )}
                </div>
                {isEditing && isSliderOptionQuestion && (
                  <QuestionnaireSliderPreview {...sliderOptionPreviewData} />
                )}
              </div>
            )}
            {/* add option button & switch for other option */}
            {isEditing && ["radio", "multi_select"].includes(question?.type) && (
              <div
                css={{
                  paddingLeft: "24px",
                  display: "flex",
                  justifyContent: "start",
                  alignItems: "center",
                  gap: "24px",
                }}
              >
                <ButtonReverse
                  onClick={addOption}
                  btnPadding="mediumSmall"
                  children="+ Add New Option"
                />
                {!isSliderOptionQuestion && (
                  <>
                    <div
                      css={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <SlideCheckbox
                        value={question?.inputConfig?.allowOther ?? false}
                        onChange={handleChangeOfAllowOtherStatus}
                      />
                      <span css={{ color: color.textIcon.secondary }}>"Other" Option</span>
                    </div>

                    {question?.inputConfig?.allowOther && (
                      <QuestionnaireInput
                        draftField={`question:${id}:other-option-name`}
                        size={QuestionnaireInputSize.verySmall}
                        placeholder='Change "Other" label here'
                        initialValue={""}
                        defaultValue={question?.inputConfig?.customOtherName || ""}
                        onChangeAction={handleChangeOfCustomOtherName}
                        hideWordCount={true}
                        maxLength={100}
                      />
                    )}
                  </>
                )}
              </div>
            )}
            {/* skip logic */}
            {hasSkipLogic && question.type === QuestionnaireQuestionType.Number && (
              <div
                css={{
                  width: "100%",
                  // padding: isEditing ? '16px' : '8px',
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {currentDisplayStrategy?.displayStrategy.displayLogicList
                  ?.filter(({ type }) => type === IQuestionnaireDisplayLogicType.Skip)
                  .flatMap(({ type, displayRuleList }) =>
                    displayRuleList?.map((rule) => (
                      <div
                        key={t.uuid()}
                        css={{
                          width: "100%",
                          padding: "4px 16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          backgroundColor: color.tag.purple.light,
                        }}
                      >
                        <SkipIcon width={14} fill={color.tag.purple.dark} />
                        <span
                          css={{
                            color: color.tag.purple.dark,
                            fontSize: fontSize.h7,
                            fontWeight: fontWeight.bold,
                          }}
                        >
                          {generateRuleText(rule, type)}
                        </span>
                      </div>
                    )),
                  )}
              </div>
            )}
            {/* display logic referring */}
            {numberDisplayLogicTextList.length > 0 &&
              question.type === QuestionnaireQuestionType.Number && (
                <div
                  css={{
                    width: "100%",
                    // padding: isEditing ? '16px' : '8px',
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {numberDisplayLogicTextList.map((text) => (
                    <div
                      key={t.uuid()}
                      css={{
                        width: "100%",
                        padding: "4px 16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        backgroundColor: color.surface.nested,
                      }}
                    >
                      <EyeIcon width={14} fill={color.textIcon.secondary} />
                      <span
                        css={{
                          color: color.textIcon.secondary,
                          fontSize: fontSize.h7,
                          fontWeight: fontWeight.bold,
                        }}
                      >
                        {text}
                      </span>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
        {/* add new question button container */}
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: "30px",
            height: "18px",
          }}
        >
          {showDragHandle && (
            <button
              onClick={handleClickAddNewQuestion}
              css={{
                fontWeight: fontWeight.thick,
                color: color.textIcon.link,
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0",
              }}
            >
              + New Question
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
